import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import posthog from "posthog-js";

import { AuthContext } from "@/contexts/authContext";
import {
  acceptAIConsent,
  createAISession,
  deleteAISession,
  getSessionHistory,
  listAISessions,
  type ContentBlock,
  type ResponseMetadata,
  type SessionResponse,
} from "@/services/aiService";
import {
  DESTINATION_FULLSTORY,
  DESTINATION_POSTHOG,
  trackCustomEvent,
} from "@/utils/analytics/tracking";

export interface ViewingContext {
  viewing_test_hash: string | null;
  page: string;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  blocks?: ContentBlock[];
  metadata?: ResponseMetadata | null;
  timestamp?: string;
  isSourceExpansion?: boolean;
}

interface ChatContextType {
  isOpen: boolean;
  toggle: () => void;
  open: () => void;
  openWithPrompt: (prompt: string) => void;
  close: () => void;
  pendingPrompt: string | null;
  clearPendingPrompt: () => void;
  isExpanded: boolean;
  toggleExpanded: () => void;
  sessionId: string | null;
  isCreatingSession: boolean;
  sessionError: boolean;
  viewingContext: ViewingContext;
  setViewingContext: (ctx: ViewingContext) => void;
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  streamingContent: string;
  setStreamingContent: (content: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  isLoadingHistory: boolean;
  sessions: SessionResponse[];
  viewingHistorySessionId: string | null;
  loadSession: (sessionId: string) => void;
  returnToLive: () => void;
  needsConsent: boolean;
  isAcceptingConsent: boolean;
  handleAcceptConsent: () => void;
}

const ChatContext = createContext<ChatContextType | null>(null);

const BOT_ID = "health_coach";

function ChatProviderInner({ children }: { children: ReactNode }) {
  const { currentUser } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // A question handed off from a recommended-question chip, sent by
  // ChatSidebar as soon as the session is ready.
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streamingContent, setStreamingContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Separate from isLoading, which tracks the live response stream. One flag
  // for both let a history fetch and a stream clear each other's state.
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [sessionError, setSessionError] = useState(false);
  const [viewingContext, setViewingContext] = useState<ViewingContext>({
    viewing_test_hash: null,
    page: "other",
  });
  const [sessions, setSessions] = useState<SessionResponse[]>([]);
  const [viewingHistorySessionId, setViewingHistorySessionId] = useState<
    string | null
  >(null);
  // Stash live messages when viewing a past session so we can restore them
  const liveMessagesRef = useRef<ChatMessage[]>([]);
  const [hasConsented, setHasConsented] = useState(
    () => currentUser?.features?.aiChatConsented ?? false
  );
  const [isAcceptingConsent, setIsAcceptingConsent] = useState(false);

  // Generation counter guards against rapid toggle race conditions.
  // Each open() increments it; stale creation callbacks check their
  // captured generation against current and bail if mismatched.
  const generationRef = useRef(0);
  // Separate generation counter for loadSession — invalidated by
  // returnToLive(), close(), and subsequent loadSession() calls.
  const loadSessionGenRef = useRef(0);
  // Ref mirrors sessionId state so endSession() always reads the latest
  // value without depending on stale closures.
  const sessionIdRef = useRef<string | null>(null);
  // Wall-clock timestamp of the active session's creation. Used to
  // decide whether a returning user can reuse an existing session
  // (within SESSION_TTL_MS) or needs a fresh one.
  const sessionStartedAtRef = useRef<number | null>(null);

  // Sessions persist across drawer close/reopen for up to 12 hours,
  // per design + dev handoff. After that, the next open() rolls a new one.
  const SESSION_TTL_MS = 12 * 60 * 60 * 1000;

  const startSession = useCallback(() => {
    const generation = ++generationRef.current;
    setIsCreatingSession(true);
    setSessionError(false);

    trackCustomEvent("ai_chat_opened", { bot_id: BOT_ID }, [
      DESTINATION_FULLSTORY,
      DESTINATION_POSTHOG,
    ]);

    createAISession(BOT_ID)
      .then((session) => {
        if (generationRef.current !== generation) return;
        sessionIdRef.current = session.session_id;
        sessionStartedAtRef.current = Date.now();
        setSessionId(session.session_id);
      })
      .catch(() => {
        if (generationRef.current !== generation) return;
        setSessionError(true);
      })
      .finally(() => {
        if (generationRef.current !== generation) return;
        setIsCreatingSession(false);
      });

    // Fetch session list in parallel (non-blocking)
    listAISessions()
      .then((list) => {
        if (generationRef.current !== generation) return;
        setSessions(list);
      })
      .catch(() => {});
  }, []);

  // Tear the session down completely: deletes the backend session and
  // clears every piece of chat state. Called on logout and when the
  // active session crosses its TTL.
  const endSession = useCallback(() => {
    generationRef.current++;
    loadSessionGenRef.current++;
    if (sessionIdRef.current) {
      deleteAISession(sessionIdRef.current).catch(() => {});
    }
    sessionIdRef.current = null;
    sessionStartedAtRef.current = null;
    setSessionId(null);
    setIsCreatingSession(false);
    setSessionError(false);
    setViewingContext({ viewing_test_hash: null, page: "other" });
    setMessages([]);
    setStreamingContent("");
    setIsLoading(false);
    setIsLoadingHistory(false);
    setIsExpanded(false);
    setIsOpen(false);
    setSessions([]);
    setViewingHistorySessionId(null);
    setPendingPrompt(null);
    liveMessagesRef.current = [];
  }, []);

  const open = useCallback(() => {
    if (!hasConsented) {
      setIsOpen(true);
      return;
    }

    const startedAt = sessionStartedAtRef.current;
    if (sessionIdRef.current !== null && startedAt !== null) {
      // Session still fresh — reuse it, just show the drawer.
      if (Date.now() - startedAt < SESSION_TTL_MS) {
        setIsOpen(true);
        return;
      }
      // Session aged out — clear state before opening a new one so the
      // user doesn't see leftover messages from the expired session.
      endSession();
    }

    setIsOpen(true);
    startSession();
  }, [hasConsented, startSession, endSession, SESSION_TTL_MS]);

  const handleAcceptConsent = useCallback(() => {
    setIsAcceptingConsent(true);
    acceptAIConsent()
      .then(() => {
        setHasConsented(true);
        startSession();
      })
      .catch(() => {
        setSessionError(true);
      })
      .finally(() => setIsAcceptingConsent(false));
  }, [startSession]);

  const loadSession = useCallback(
    (targetSessionId: string) => {
      const gen = ++loadSessionGenRef.current;

      // Stash live messages if switching away from live session
      if (!viewingHistorySessionId) {
        liveMessagesRef.current = messages;
      }
      setViewingHistorySessionId(targetSessionId);
      setMessages([]);
      setIsLoadingHistory(true);

      getSessionHistory(targetSessionId)
        .then((history) => {
          if (loadSessionGenRef.current !== gen) return;
          setMessages(history);
        })
        .catch(() => {
          if (loadSessionGenRef.current !== gen) return;
          setMessages([
            {
              role: "assistant",
              content: "Could not load conversation history.",
            },
          ]);
        })
        .finally(() => {
          if (loadSessionGenRef.current !== gen) return;
          setIsLoadingHistory(false);
        });
    },
    [viewingHistorySessionId, messages]
  );

  const returnToLive = useCallback(() => {
    loadSessionGenRef.current++;
    setViewingHistorySessionId(null);
    setMessages(liveMessagesRef.current);
    liveMessagesRef.current = [];
    // An in-flight loadSession skips its own cleanup once invalidated above.
    setIsLoadingHistory(false);
  }, []);

  // open() can roll a new session (which clears state), so stash the prompt
  // after it. A chip always lands in the live conversation, never a past one.
  const openWithPrompt = useCallback(
    (prompt: string) => {
      open();
      if (viewingHistorySessionId) returnToLive();
      setPendingPrompt(prompt);
    },
    [open, returnToLive, viewingHistorySessionId]
  );

  const clearPendingPrompt = useCallback(() => setPendingPrompt(null), []);

  // Closing the drawer hides the UI but keeps the conversation alive
  // (per design + dev handoff: end the session only on logout or after
  // SESSION_TTL_MS elapses, not when the user closes the panel).
  const close = useCallback(() => {
    trackCustomEvent("ai_chat_closed", {}, [
      DESTINATION_FULLSTORY,
      DESTINATION_POSTHOG,
    ]);
    setStreamingContent("");
    setIsExpanded(false);
    setIsOpen(false);
    setPendingPrompt(null);
  }, []);

  // End the session when the user logs out (currentUser transitions to
  // null) so the next user doesn't inherit chat state.
  const wasAuthenticatedRef = useRef(!!currentUser);
  useEffect(() => {
    if (wasAuthenticatedRef.current && !currentUser) {
      endSession();
    }
    wasAuthenticatedRef.current = !!currentUser;
  }, [currentUser, endSession]);

  const toggle = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      open();
    }
  }, [isOpen, close, open]);

  const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), []);

  return (
    <ChatContext.Provider
      value={{
        isOpen,
        toggle,
        open,
        openWithPrompt,
        close,
        pendingPrompt,
        clearPendingPrompt,
        isExpanded,
        toggleExpanded,
        sessionId,
        isCreatingSession,
        sessionError,
        viewingContext,
        setViewingContext,
        messages,
        setMessages,
        streamingContent,
        setStreamingContent,
        isLoading,
        setIsLoading,
        isLoadingHistory,
        sessions,
        viewingHistorySessionId,
        loadSession,
        returnToLive,
        needsConsent: !hasConsented,
        isAcceptingConsent,
        handleAcceptConsent,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

const isLocalhost = window.location.hostname === "localhost";

export function ChatProvider({ children }: { children: ReactNode }) {
  const { currentUser } = useContext(AuthContext);

  // Server-side flag check (ad-blocker proof), client-side PostHog as fallback
  const serverFlagEnabled = currentUser?.features?.aiChatEnabled;

  const [flagReady, setFlagReady] = useState(
    isLocalhost || !!serverFlagEnabled
  );

  useEffect(() => {
    if (serverFlagEnabled) {
      setFlagReady(true);
    } else if (!isLocalhost) {
      posthog.onFeatureFlags(() => setFlagReady(true));
    }
  }, [serverFlagEnabled]);

  const aiEnabled =
    isLocalhost ||
    serverFlagEnabled ||
    posthog.isFeatureEnabled("evvy-ai-enabled");

  if (!flagReady || !aiEnabled) {
    return <>{children}</>;
  }

  return <ChatProviderInner>{children}</ChatProviderInner>;
}

export function useChat(): ChatContextType | null {
  return useContext(ChatContext);
}
